/**
 * Il fotogramma, con dentro il riquadro della predizione.
 *
 * **Si apre subito e si ingrandisce fino al pixel.** L'operatore decide
 * guardando: se per vedere un isolatore deve aspettare il caricamento di 60
 * Mpixel, il tempo della decisione lo detta l'attesa, non il giudizio.
 *
 * **Si arriva gia' sul riquadro.** Portare la vista sull'anomalia e' cio' che
 * l'operatore farebbe comunque come primo gesto, ogni volta, per centinaia di
 * candidati: farglielo fare a mano e' rubargli quel gesto moltiplicato per la
 * giornata.
 *
 * **Il riquadro si misura in pixel dell'immagine, non dello schermo.** E' cio'
 * che la consegna vuole, ed e' l'unica misura che non dipende
 * dall'ingrandimento al momento in cui si guarda. Per questo la conversione
 * passa sempre dal viewport di OpenSeadragon e mai dalle proporzioni della
 * tela: quelle sono giuste solo a immagine intera, cioe' quasi mai quando si
 * ridisegna.
 */
import OpenSeadragon from 'openseadragon'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { tx } from './i18n/t'
import { asJpeg, levelOf, open, tileIndex } from './pyramid'
import type { Box, Point } from './types'

/** Che cosa si sta disegnando: il riquadro di sempre, o il contorno a vertici. */
export type Shape = 'box' | 'polygon'

/** Un riquadro gia' registrato, con l'etichetta e il gettone di colore. */
export interface Mark {
  box: Box
  label: string
  /** Il nome del gettone (`sev-2`, `ink-3`): il colore viene dalla scala, non da qui. */
  tone: string
}

export interface ViewerProps {
  tilesUrl: string
  /** Dove il servizio serve l'indice della piramide (GI-269); senza, si
   *  ricostruisce dall'archivio un livello per volta. */
  indexUrl?: string | null
  width: number
  height: number
  tile: number
  levels: number
  box: Box | null
  /** Se l'operatore sta disegnando. */
  drawing?: boolean
  /** Che cosa disegna: il riquadro, salvo che si dica il contorno (GI-228). */
  shape?: Shape
  onDrawn?: (box: Box) => void
  /** Il contorno gia' disegnato, da mostrare sul fotogramma. */
  polygon?: Point[] | null
  /** Le anomalie gia' registrate sul fotogramma, ciascuna col suo tono: si
   *  disegnano sotto il riquadro di lavoro, per sapere dove si e' gia' guardato. */
  marks?: Mark[]
  onDrawnPolygon?: (points: Point[]) => void
  /** Che cosa dice l'etichetta sul fotogramma: codice, rango, margine. */
  label?: string
  /**
   * Le righe sotto l'etichetta (§ 6.2): file, camera, ora dello scatto,
   * elemento. Quel che si guarda insieme all'immagine per sapere *quale*
   * immagine e' — senza, due scatti dello stesso palo si confondono, e chi
   * torna sul fotogramma dopo un'ora non sa se e' lo stesso (GI-265).
   */
  caption?: string[]
  /**
   * La scheda del fotogramma, in basso a sinistra (GI-266): il bollino della
   * camera, il nome del file, l'ora, e una riga in piu' — l'elemento, per il
   * banco. E' la risposta a «*quale* fotogramma sto guardando», e sta
   * nell'angolo opposto alla scala perche' sono due domande diverse.
   */
  frame?: FrameCard | null
  /** Il cartellino sul riquadro del candidato: codice e rango scritti *sulla*
   *  trovata, dove sta lo sguardo, e non in un angolo (GI-266). Sparisce con i
   *  disegni, perche' e' un disegno. */
  boxLabel?: string
  /** Dove riferire quanti vertici del contorno sono gia' posati: la palette
   *  degli strumenti lo scrive accanto al modo, perche' un disegno a meta' che
   *  non si vede e' un disegno che si dimentica. */
  onDrawingProgress?: (vertices: number) => void
  /**
   * L'alternativa testuale del fotogramma.
   *
   * «Immagine» non e' un'alternativa: chi non vede il fotogramma deve sapere di
   * quale codice, di quale rango e di quale margine si sta parlando, altrimenti
   * la schermata gli dice che c'e' qualcosa da guardare e non che cosa.
   */
  alt?: string
  /** Il nome del modo in corso. Nullo quando si e' nel modo base.
   *
   * Un modo che non si vede e' una sorgente di battute sbagliate: da qui il
   * cantuccio interno acceso e il nome scritto in basso a sinistra (§ 6.2). */
  mode?: string | null
  /** Vero mentre l'operatore tiene premuto lo spazio: la grafica sparisce e
   *  restano i pixel su cui si decide. */
  hidden?: boolean
  /** L'ordine di inquadratura, con il colpo che lo distingue dal precedente:
   *  premere due volte lo stesso tasto deve rifare il movimento. */
  view?: { how: 'uno-a-uno' | 'inquadra' | 'trovata'; nonce: number } | null
}

/** Che cosa scrive la scheda del fotogramma. Cio' che manca non si scrive. */
export interface FrameCard {
  /** Il nome della camera, per il bollino: «RETRO1», «2». */
  camera?: string | null
  filename: string | null
  /** L'ora dello scatto, gia' scritta come si legge. */
  when?: string | null
  /** Una riga in piu' sotto: l'elemento, per il banco. */
  extra?: string | null
}

/** Quanto respiro lasciare intorno al riquadro all'apertura: il difetto si
 *  giudica anche da cio' che gli sta accanto — un conduttore isolato non dice
 *  se il ramo lo tocca. */
const MARGIN = 3

/** Dopo quanti secondi di lettura la scritta comincia a contare: prima e'
 *  un'attesa normale, dopo e' un archivio lento e va detto. */
const SLOW_AFTER_S = 3

/**
 * Il riquadro fra due punti dell'immagine.
 *
 * Si normalizza perche' si disegna in tutte e quattro le direzioni: chi tira
 * verso l'alto a sinistra produrrebbe altrimenti una larghezza negativa, e il
 * servizio la rifiuterebbe con un messaggio che non spiega nulla a chi ha solo
 * trascinato al contrario.
 *
 * Si taglia sui bordi del fotogramma: il trascinamento esce facilmente, e un
 * riquadro fuori la consegna lo rifiuterebbe a giornata finita.
 */
export function boxBetween(
  from: { x: number; y: number },
  to: { x: number; y: number },
  width: number,
  height: number,
): Box {
  const left = Math.max(0, Math.min(from.x, to.x))
  const top = Math.max(0, Math.min(from.y, to.y))
  const right = Math.min(width, Math.max(from.x, to.x))
  const bottom = Math.min(height, Math.max(from.y, to.y))
  return {
    x: Math.round(left),
    y: Math.round(top),
    width: Math.max(0, Math.round(right - left)),
    height: Math.max(0, Math.round(bottom - top)),
  }
}

/** Il compito di caricamento di un tassello, come lo passa OpenSeadragon.
 *
 *  Descritto qui e non preso dai tipi pubblicati: quelli modellano le sorgenti
 *  che hanno un indirizzo per tassello (DZI, IIIF) e non la sorgente con
 *  caricamento proprio, che la libreria invece sostiene. */
interface TileJob {
  src: string
  finish(image: HTMLImageElement | null, request: null, error?: string | null): void
}

interface RangeSource {
  width: number
  height: number
  tileSize: number
  tileOverlap: number
  minLevel: number
  maxLevel: number
  getTileUrl(level: number, x: number, y: number): string
  downloadTileStart(context: TileJob): void
}

/** L'unico punto in cui si forza un tipo, e si vede subito perche'. */
function rangeSource(source: RangeSource): OpenSeadragon.Options['tileSources'] {
  return source as unknown as OpenSeadragon.Options['tileSources']
}

export function Viewer({
  tilesUrl,
  indexUrl = null,
  width,
  height,
  tile,
  levels,
  box,
  drawing = false,
  shape = 'box',
  onDrawn,
  polygon = null,
  marks = [],
  onDrawnPolygon,
  label,
  caption = [],
  frame = null,
  boxLabel,
  onDrawingProgress,
  alt,
  mode = null,
  hidden = false,
  view = null,
}: ViewerProps): JSX.Element {
  const marksKey = JSON.stringify(marks)
  const holder = useRef<HTMLDivElement>(null)
  const [viewer, setViewer] = useState<OpenSeadragon.Viewer | null>(null)
  const [ready, setReady] = useState(false)
  const [unreadable, setUnreadable] = useState<string | null>(null)
  // Da quanti secondi si legge. Dopo i primi la scritta lo dice: su un archivio
  // lento l'indice arriva in dieci secondi, e dieci secondi di «Leggo…» che
  // non cambia si leggono come una pagina ferma (staging, 8.9.2026).
  const [waited, setWaited] = useState(0)
  useEffect(() => {
    if (ready || unreadable !== null) return
    const since = Date.now()
    const timer = setInterval(() => setWaited(Math.round((Date.now() - since) / 1000)), 1000)
    return () => {
      clearInterval(timer)
      setWaited(0)
    }
  }, [ready, unreadable, tilesUrl])
  // La scala corrente, pixel dell'immagine per pixel dello schermo. Si scrive
  // sul fotogramma (§ 6.2, «IMG_0442 · 1:1») perche' la nitidezza si giudica
  // solo sapendo a che ingrandimento si sta guardando.
  const [zoom, setZoom] = useState<number | null>(null)

  useEffect(() => {
    const node = holder.current
    if (!node) return
    let made: OpenSeadragon.Viewer | null = null
    let alive = true
    const urls: string[] = []

    setReady(false)
    setUnreadable(null)
    setZoom(null)
    void open(tilesUrl, indexUrl).then((pyramid) => {
      if (!alive) return

      made = OpenSeadragon({
        element: node,
        // Nessun elemento decorativo dalla rete: l'istanza puo' stare in un
        // perimetro chiuso, e i bottoni con l'icona presa da fuori li'
        // resterebbero vuoti.
        showNavigationControl: false,
        showNavigator: true,
        navigatorPosition: 'BOTTOM_RIGHT',
        gestureSettingsMouse: { clickToZoom: false },
        // Il fotogramma e' grande e i tasselli arrivano dall'archivio: senza
        // questo il primo movimento ne chiederebbe centinaia insieme.
        imageLoaderLimit: 6,
        tileSources: rangeSource({
          width,
          height,
          tileSize: tile,
          tileOverlap: 0,
          minLevel: 0,
          maxLevel: levels - 1,
          getTileUrl: (level: number, x: number, y: number) => `${level}/${x}/${y}`,
          // I tasselli non hanno un indirizzo proprio: stanno dentro un unico
          // oggetto, e si prendono per intervallo di byte. Per questo il
          // caricamento e' nostro e non del navigatore.
          downloadTileStart(context: TileJob): void {
            const [rawLevel, rawX, rawY] = context.src.split('/').map(Number)
            const depth = levelOf(rawLevel ?? 0, pyramid.levels.length)
            const chosen = pyramid.levels[depth]
            if (!chosen) {
              context.finish(null, null, `livello ${rawLevel} inesistente`)
              return
            }
            pyramid
              .bytes(depth, tileIndex(chosen, rawX ?? 0, rawY ?? 0))
              .then((bytes) => {
                const blob = new Blob([asJpeg(pyramid.tables, bytes)], { type: 'image/jpeg' })
                const url = URL.createObjectURL(blob)
                urls.push(url)
                const image = new Image()
                image.onload = () => context.finish(image, null, null)
                image.onerror = () => context.finish(null, null, 'tassello illeggibile')
                image.src = url
              })
              .catch((error: unknown) => {
                context.finish(null, null, error instanceof Error ? error.message : 'errore')
              })
          },
        }),
      })

      if (box) {
        const overlay = document.createElement('div')
        // Doppio tratto: sotto l'alone nero, sopra il tratto di lavoro. Un
        // contorno senza alone sparisce contro un cielo bianco, ed e' proprio
        // sopra il cielo che passa mezzo traliccio (§ 08).
        overlay.className = 'box'
        if (boxLabel) {
          // Il cartellino sta attaccato al riquadro, sopra il suo bordo alto:
          // codice e rango si leggono dove si guarda, senza cercarli in un
          // angolo. Nel tono dell'attivo, come il riquadro.
          const tag = document.createElement('span')
          tag.className = 'etichetta segno-etichetta cartellino'
          tag.textContent = boxLabel
          overlay.appendChild(tag)
        }
        made.addOverlay({
          element: overlay,
          // Le coordinate di OpenSeadragon vanno da 0 a 1 sul lato lungo:
          // l'immagine si misura in frazioni di se stessa, non in pixel.
          location: new OpenSeadragon.Rect(
            box.x / width,
            box.y / width,
            box.width / width,
            box.height / width,
          ),
        })
        made.addHandler('open', () => {
          made?.viewport.fitBounds(around(box, width), true)
        })
      }
      for (const mark of marks) {
        const overlay = document.createElement('div')
        overlay.className = 'box segno'
        // Il tono viene dalla scala dei ranghi, per nome di gettone: e' lo
        // stesso colore che il segno ha sulla mappa e nella coda.
        overlay.style.borderColor = `var(--${mark.tone})`
        overlay.style.color = `var(--${mark.tone})`
        const label = document.createElement('span')
        label.className = 'etichetta segno-etichetta'
        label.textContent = mark.label
        overlay.appendChild(label)
        made.addOverlay({
          element: overlay,
          location: new OpenSeadragon.Rect(
            mark.box.x / width,
            mark.box.y / width,
            mark.box.width / width,
            mark.box.height / width,
          ),
        })
      }
      if (polygon && polygon.length >= 3) {
        // Il contorno sta in un SVG che copre il suo riquadro: OpenSeadragon
        // sposta e scala il riquadro, e i vertici seguono nel suo `viewBox`.
        const bounds = boundsOf(polygon)
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        svg.setAttribute('class', 'contorno')
        svg.setAttribute('viewBox', `${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`)
        svg.setAttribute('preserveAspectRatio', 'none')
        const shape_ = document.createElementNS('http://www.w3.org/2000/svg', 'polygon')
        shape_.setAttribute('points', polygon.map((one) => `${one.x},${one.y}`).join(' '))
        shape_.setAttribute('vector-effect', 'non-scaling-stroke')
        svg.appendChild(shape_)
        // OpenSeadragon vuole un elemento HTML: il contenitore prende posizione
        // e misura, l'SVG dentro riempie e scala.
        const frame = document.createElement('div')
        frame.className = 'contorno-cornice'
        frame.appendChild(svg)
        made.addOverlay({
          element: frame,
          location: new OpenSeadragon.Rect(
            bounds.x / width,
            bounds.y / width,
            bounds.width / width,
            bounds.height / width,
          ),
        })
      }
      // Finche' il primo livello non e' disegnato si dice che si sta leggendo:
      // mezzo fotogramma senza avviso si legge come un fotogramma tagliato.
      made.addHandler('open', () => alive && setReady(true))
      // Un'apertura fallita che non dice niente lascia «Leggo…» per sempre.
      made.addHandler('open-failed', (event) => {
        if (!alive) return
        const said = (event as { message?: string }).message
        setUnreadable(said && said.length > 0 ? said : 'open-failed')
      })
      // La scala si legge dal viewport e non si ricalcola dalla tela: e' la
      // stessa conversione che fanno i riquadri, e due conversioni divergono.
      const scale = (): void => {
        if (!alive || !made) return
        setZoom(made.viewport.viewportToImageZoom(made.viewport.getZoom(true)))
      }
      made.addHandler('open', scale)
      made.addHandler('zoom', scale)
      made.addHandler('resize', scale)
      // `zoom` parte all'inizio della molla, quando la scala corrente non si e'
      // ancora mossa: letta li', la cifra restava quella di prima finche' un
      // altro gesto non la aggiornava (rotella nella ricerca libera, 8.9.2026).
      // Si rilegge a molla ferma.
      made.addHandler('animation-finish', scale)
      setViewer(made)
    }).catch((error: unknown) => {
      // Un archivio che non risponde non e' un guasto della pagina: si dice,
      // e la promessa non resta a mezz'aria (la catena di montaggio la conta
      // come errore non gestito).
      if (alive) setUnreadable(error instanceof Error ? error.message : 'errore')
    })

    return () => {
      alive = false
      made?.destroy()
      setViewer(null)
      // Gli indirizzi temporanei non si liberano da soli: su una giornata di
      // lavoro sarebbero migliaia di tasselli trattenuti in memoria.
      urls.forEach((url) => URL.revokeObjectURL(url))
    }
    // I segni si confrontano per contenuto: un elenco nuovo a ogni ridisegno
    // con gli stessi riquadri non deve rifare il visualizzatore — rifarlo
    // mentre React riconcilia toglieva nodi che non erano piu' suoi, e la
    // pagina restava bianca (visto dal vivo il 7.9.2026).
  }, [tilesUrl, width, height, tile, levels, box, polygon, marksKey, boxLabel])

  // Le inquadrature: 1:1 perche' e' l'unica scala in cui si giudica la
  // nitidezza, «inquadra» per riprendere l'orientamento quando ci si e' persi,
  // «trovata» per tornare al riquadro senza cercarlo.
  useEffect(() => {
    if (!viewer || !view) return
    if (view.how === 'uno-a-uno') {
      viewer.viewport.zoomTo(viewer.viewport.imageToViewportZoom(1))
      return
    }
    if (view.how === 'inquadra') {
      viewer.viewport.goHome()
      return
    }
    if (box) viewer.viewport.fitBounds(around(box, width), false)
  }, [viewer, view, box, width])

  return (
    <div className="cantuccio">
      <div
        className={[
          'viewer',
          drawing ? 'disegno' : '',
          mode ? 'in-modo' : '',
          hidden ? 'senza-disegni' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        // E' un'immagine, e va annunciata come tale: quel che ci sta sopra —
        // riquadro, etichetta, navigatore — descrive la stessa cosa, e leggerlo
        // pezzo per pezzo direbbe meno dell'alternativa intera.
        role={alt ? 'img' : undefined}
        aria-label={alt}
      >
        {/* OpenSeadragon ha un nodo tutto suo, che React non popola: due
            padroni dello stesso nodo — React che toglie l'etichetta,
            OpenSeadragon che toglie la tela — si contendevano i figli allo
            smontaggio, e la pagina restava bianca (7.9.2026). */}
        <div className="viewer-osd" ref={holder} aria-busy={!ready} />
        {!ready && unreadable === null && (
          <p className="tela-lettura" role="status">
            {waited >= SLOW_AFTER_S ? tx('viewer.readingFor', { s: String(waited) }) : tx('viewer.reading')}
          </p>
        )}
        {unreadable !== null && (
          <p className="tela-lettura" role="alert">
            {tx('viewer.unreadable', { why: unreadable })}
          </p>
        )}
        {drawing && viewer && shape === 'box' && (
          <Drawing viewer={viewer} width={width} height={height} onDrawn={onDrawn} />
        )}
        {drawing && viewer && shape === 'polygon' && (
          <PolygonDrawing
            viewer={viewer}
            width={width}
            height={height}
            onDrawn={onDrawnPolygon}
            onProgress={onDrawingProgress}
          />
        )}
        {/* Quattro angoli e niente in mezzo (§ 6.2). In alto a sinistra gli
            strumenti; in alto a destra la scala — grande, perche' e' il
            numero che dice se la nitidezza che si vede e' quella del
            fotogramma — con le dimensioni dello scatto sotto; in basso a
            sinistra il modo e la scheda del fotogramma; in basso a destra il
            navigatore. */}
        {(label || caption.length > 0 || zoom !== null) && !hidden && (
          <div className="etichetta angolo-alto-destra hud">
            {label && <span className="hud-titolo">{label}</span>}
            {caption.map((line) => (
              <span key={line}>{line}</span>
            ))}
            {zoom !== null && (
              <>
                <span className="hud-didascalia">{tx('viewer.scale')}</span>
                <span className="hud-scala">{scaleOf(zoom)}</span>
                <span className="hud-misure">
                  {width} × {height} px
                </span>
              </>
            )}
          </div>
        )}
        <div className="hud-angolo hud-basso-sinistra">
          {mode && (
            // Il nome del modo resta anche con lo spazio premuto: sparire
            // insieme ai disegni lascerebbe l'operatore in un modo invisibile.
            <span className="etichetta modo">◆ {mode}</span>
          )}
          {frame !== null && !hidden && (
            <div className="etichetta hud hud-scheda">
              <span className="hud-didascalia">{tx('viewer.frame')}</span>
              <span className="hud-scheda-riga">
                {frame.camera && <span className="hud-camera">{frame.camera}</span>}
                <span className="hud-file">{frame.filename ?? '—'}</span>
              </span>
              {(frame.when || frame.extra) && (
                <span className="hud-scheda-riga tenue">
                  {frame.when && <span>{frame.when}</span>}
                  {frame.extra && <span>{frame.extra}</span>}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** La scala come la si legge: `1:1` al pixel, altrimenti la percentuale.
 *
 *  `1:1` e non «100 %» perche' e' l'unica scala che conta per giudicare la
 *  nitidezza, e va riconosciuta a colpo d'occhio; le altre sono orientamento. */
export function scaleOf(zoom: number): string {
  if (Math.abs(zoom - 1) < 0.02) return '1:1'
  const percent = zoom * 100
  return `${percent < 10 ? percent.toFixed(1) : Math.round(percent)} %`
}

/** Il riquadro con il respiro attorno, in coordinate del visualizzatore. */
function around(box: Box, width: number): OpenSeadragon.Rect {
  return new OpenSeadragon.Rect(
    (box.x - box.width * MARGIN) / width,
    (box.y - box.height * MARGIN) / width,
    (box.width * (1 + 2 * MARGIN)) / width,
    (box.height * (1 + 2 * MARGIN)) / width,
  )
}

/**
 * La tela su cui si trascina il riquadro nuovo.
 *
 * Sta sopra il visualizzatore e ne prende i gesti: trascinare per spostare
 * l'immagine e trascinare per disegnare sono lo stesso gesto, e non possono
 * convivere. Due stati dichiarati sono meglio di un gesto ambiguo, dove
 * l'operatore scopre quale dei due ha fatto solo dopo averlo fatto.
 */
function Drawing({
  viewer,
  width,
  height,
  onDrawn,
}: {
  viewer: OpenSeadragon.Viewer
  width: number
  height: number
  onDrawn?: (box: Box) => void
}): JSX.Element {
  const [from, setFrom] = useState<{ x: number; y: number } | null>(null)
  const [to, setTo] = useState<{ x: number; y: number } | null>(null)

  /** Dal pixel dello schermo al pixel dell'immagine. */
  function at(event: React.PointerEvent<HTMLDivElement>): { x: number; y: number } {
    const bounds = event.currentTarget.getBoundingClientRect()
    const pixel = new OpenSeadragon.Point(event.clientX - bounds.left, event.clientY - bounds.top)
    const point = viewer.viewport.viewportToImageCoordinates(viewer.viewport.pointFromPixel(pixel))
    return { x: point.x, y: point.y }
  }

  const rect = from && to ? boxBetween(from, to, width, height) : null
  const shown = rect ? onScreen(viewer, rect) : null

  return (
    <div
      className="tela"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        const start = at(event)
        setFrom(start)
        setTo(start)
      }}
      onPointerMove={(event) => {
        if (from) setTo(at(event))
      }}
      onPointerUp={(event) => {
        const finished = from ? boxBetween(from, at(event), width, height) : null
        setFrom(null)
        setTo(null)
        // Un riquadro senza area e' un clic, non un disegno: e' il gesto di chi
        // ha sbagliato, e non deve diventare un verdetto sbagliato.
        if (finished && finished.width > 0 && finished.height > 0) onDrawn?.(finished)
      }}
    >
      {shown && (
        <div
          className="box in-corso"
          style={{
            left: `${shown.left}px`,
            top: `${shown.top}px`,
            width: `${shown.width}px`,
            height: `${shown.height}px`,
          }}
        />
      )}
    </div>
  )
}

/** Dove disegnare, sullo schermo, un riquadro misurato sull'immagine. */
function onScreen(
  viewer: OpenSeadragon.Viewer,
  box: Box,
): { left: number; top: number; width: number; height: number } {
  const corner = viewer.viewport.pixelFromPoint(
    viewer.viewport.imageToViewportCoordinates(new OpenSeadragon.Point(box.x, box.y)),
    true,
  )
  const opposite = viewer.viewport.pixelFromPoint(
    viewer.viewport.imageToViewportCoordinates(
      new OpenSeadragon.Point(box.x + box.width, box.y + box.height),
    ),
    true,
  )
  return {
    left: corner.x,
    top: corner.y,
    width: opposite.x - corner.x,
    height: opposite.y - corner.y,
  }
}

/** Il riquadro che contiene i vertici. */
export function boundsOf(points: Point[]): Box {
  const xs = points.map((one) => one.x)
  const ys = points.map((one) => one.y)
  const left = Math.min(...xs)
  const top = Math.min(...ys)
  return { x: left, y: top, width: Math.max(...xs) - left, height: Math.max(...ys) - top }
}

/**
 * Il contorno chiuso, se e' un contorno.
 *
 * Si taglia sui bordi e si arrotonda come per il riquadro; si tolgono i vertici
 * ripetuti, che un doppio clic lascia sempre; e sotto tre vertici, o con
 * area nulla, non c'e' un dentro — e un contorno senza dentro non insegna
 * dove sta l'anomalia. Il servizio rifiuterebbe lo stesso, ma a parole
 * proprie: qui si evita di mandarglielo.
 */
export function closedPolygon(points: Point[], width: number, height: number): Point[] | null {
  const clean: Point[] = []
  for (const one of points) {
    const next = {
      x: Math.round(Math.max(0, Math.min(width, one.x))),
      y: Math.round(Math.max(0, Math.min(height, one.y))),
    }
    const last = clean[clean.length - 1]
    if (last && last.x === next.x && last.y === next.y) continue
    clean.push(next)
  }
  const first = clean[0]
  const tail = clean[clean.length - 1]
  if (clean.length > 1 && first && tail && first.x === tail.x && first.y === tail.y) clean.pop()
  if (clean.length < 3) return null
  let twice = 0
  for (let index = 0; index < clean.length; index += 1) {
    const a = clean[index]
    const b = clean[(index + 1) % clean.length]
    if (a && b) twice += a.x * b.y - b.x * a.y
  }
  return Math.abs(twice) / 2 > 0 ? clean : null
}

/** Quanto vicino al primo vertice, sullo schermo, un clic chiude il contorno. */
const CLOSING_PX = 8

/**
 * La tela su cui si posano i vertici del contorno (GI-228).
 *
 * Un clic per vertice; Invio, doppio clic o un clic sul primo vertice chiudono;
 * Backspace toglie l'ultimo. La tastiera si ascolta dalla finestra e non dalla
 * tela — che non ha il fuoco — e solo per i due tasti che il banco non usa:
 * Invio e Backspace non sono verdetti, e non lo diventano.
 */
function PolygonDrawing({
  viewer,
  width,
  height,
  onDrawn,
  onProgress,
}: {
  viewer: OpenSeadragon.Viewer
  width: number
  height: number
  onDrawn?: (points: Point[]) => void
  onProgress?: (vertices: number) => void
}): JSX.Element {
  const [points, setPoints] = useState<Point[]>([])
  const [cursor, setCursor] = useState<Point | null>(null)
  const pointsNow = useRef<Point[]>([])
  pointsNow.current = points

  // Quanti vertici ci sono lo si dice fuori a ogni cambiamento, e zero
  // all'uscita: un conteggio che restasse appeso direbbe «3 vertici» su un
  // contorno che non c'e' piu'.
  useEffect(() => {
    onProgress?.(points.length)
    return () => onProgress?.(0)
  }, [points.length, onProgress])

  function at(event: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>): Point {
    const bounds = event.currentTarget.getBoundingClientRect()
    const pixel = new OpenSeadragon.Point(event.clientX - bounds.left, event.clientY - bounds.top)
    const point = viewer.viewport.viewportToImageCoordinates(viewer.viewport.pointFromPixel(pixel))
    return { x: point.x, y: point.y }
  }

  function screen(point: Point): { x: number; y: number } {
    const pixel = viewer.viewport.pixelFromPoint(
      viewer.viewport.imageToViewportCoordinates(new OpenSeadragon.Point(point.x, point.y)),
      true,
    )
    return { x: pixel.x, y: pixel.y }
  }

  function close(): void {
    const finished = closedPolygon(pointsNow.current, width, height)
    setPoints([])
    setCursor(null)
    if (finished) onDrawn?.(finished)
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault()
        close()
      } else if (event.key === 'Backspace') {
        event.preventDefault()
        setPoints((were) => were.slice(0, -1))
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
    // `close` legge i vertici da un riferimento: l'ascolto si arma una volta.
  }, [])

  const drawn = points.map(screen)
  const first = drawn[0]
  const path = [...drawn, ...(cursor ? [screen(cursor)] : [])]

  return (
    <div
      className="tela"
      onPointerDown={(event) => {
        const here = at(event)
        if (first && points.length >= 3) {
          const pixel = screen(here)
          if (Math.hypot(pixel.x - first.x, pixel.y - first.y) <= CLOSING_PX) {
            close()
            return
          }
        }
        setPoints((were) => [...were, here])
      }}
      onPointerMove={(event) => setCursor(at(event))}
      onDoubleClick={() => close()}
    >
      <svg className="contorno-in-corso" aria-hidden="true">
        {path.length > 1 && (
          <polyline points={path.map((one) => `${one.x},${one.y}`).join(' ')} />
        )}
        {drawn.map((one, index) => (
          <circle key={index} cx={one.x} cy={one.y} r={index === 0 ? 5 : 3} />
        ))}
      </svg>
    </div>
  )
}
